import { useState } from 'react';
import { Stack } from '@mui/material';
import { useRecoilValue } from 'recoil';
import {
  ExpandableContentButton,
  ExpandableContentWrapper,
} from 'components/shared';
import { CanvasGroupLinkButton } from 'components/canvas/groups';
import { SynthOIPutCallImpactGraph } from 'components/equityhub/EquityGraph/SynthOIPutCallImpactGraph';
import { SynthOIPutCallImpactSettings } from 'components/equityhub/EquityGraph/SynthOIPutCallImpactSettings';
import { SynthOIPutCallImpactProvider } from 'components/equityhub/EquityGraph/SynthOIPutCallImpactContext';
import { SynthOIPutCallImpactZoomOutButton } from 'components/equityhub/EquityGraph/SynthOIPutCallImpactZoomOutButton';
import { equitySymbolsState } from 'states';
import { CanvasContentId } from 'types/canvas';
import { CanvasComponentConfig } from 'types/canvas';
import {
  SynthOIPutCallImpactConfig,
  DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG,
} from 'types';
import CanvasSymSelector from './CanvasSymSelector';

interface CanvasSynthOIPutCallImpactProps {
  id: number;
  sym: string;
  config: CanvasComponentConfig;
  onSymChange: (sym: string) => void;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
}

const CanvasSynthOIPutCallImpact = ({
  id,
  sym,
  config: canvasConfig,
  onSymChange,
  onConfigChange,
}: CanvasSynthOIPutCallImpactProps) => {
  const ehSymbols = useRecoilValue(equitySymbolsState);
  const symOptions = ehSymbols.map((s) => ({ symbol: s.symbol, name: s.name }));

  const [localConfig, setLocalConfig] = useState<SynthOIPutCallImpactConfig>(
    () => ({
      ...DEFAULT_SYNTH_OI_PC_IMPACT_CONFIG,
      ...(canvasConfig as Partial<SynthOIPutCallImpactConfig>),
    }),
  );

  const handleConfigChange = (patch: Partial<SynthOIPutCallImpactConfig>) => {
    setLocalConfig((prev) => {
      const next = { ...prev, ...patch };
      onConfigChange(patch);
      return next;
    });
  };

  const expandableContentButtonType = `${CanvasContentId.SYNTH_OI_PUT_CALL_IMPACT_CHART}-${id}`;

  return (
    <SynthOIPutCallImpactProvider
      selectedSym={sym}
      config={localConfig}
      onConfigChange={handleConfigChange}
    >
      <ExpandableContentWrapper
        type={expandableContentButtonType}
        styleOverrides={{ p: 1, height: '100%' }}
      >
        <Stack
          direction="column"
          spacing={1}
          sx={{ height: '100%', width: '100%' }}
        >
          <Stack
            sx={{
              px: 1,
              py: 0.5,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <CanvasSymSelector
              sym={sym}
              options={symOptions}
              onSymChange={onSymChange}
            />
            <SynthOIPutCallImpactZoomOutButton />
            <SynthOIPutCallImpactSettings />
            <CanvasGroupLinkButton
              componentId={id}
              contentId={CanvasContentId.SYNTH_OI_PUT_CALL_IMPACT_CHART}
            />
            <ExpandableContentButton type={expandableContentButtonType} />
          </Stack>
          <SynthOIPutCallImpactGraph />
        </Stack>
      </ExpandableContentWrapper>
    </SynthOIPutCallImpactProvider>
  );
};

export default CanvasSynthOIPutCallImpact;
